import { GroupSettings } from '@/types/user';

export type UserAvatar = {
  avatarName?: string;
  avatarColor?: string;
};

export type Streak = {
  day: string;
  order: number;
  eventStatus: false;
  translate: string;
};

export type UnitProgress = {
  unitId: number;
  unitName: string;
  order: number;
  totalTopics: number;
  totalActivites: number;
  completedTopics: number;
  completedActivities: number;
  hasFailedActivities?: boolean;
  progress: number;
  achievedStars: number;
  totalStars: number;
};

export enum UserStatusEnum {
  INACTIVE = 'INACTIVE',
  LEVEL_TEST_INCOMPLETE = 'LEVEL_TEST_INCOMPLETE',
  ACTIVE = 'ACTIVE',
  RESET_PASSWORD = 'RESET_PASSWORD',
  LEVEL_UP = 'LEVEL_UP',
  LEVEL_DOWN = 'LEVEL_DOWN',
  LEVEL_COMPLETED_BLOCKED = 'LEVEL_COMPLETED_BLOCKED',
}

export interface StudentDetails {
  studentData: {
    firstName: string;
    lastName: string;
    schoolName: string;
    nativeLanguage: string;
    interfaceLanguage: string;
    fromToLanguage: string;
    grade: string;
    email: string;
    username: string;
    groupName: string;
    userStatus?: UserStatusEnum;
    avatar?: UserAvatar;
    /** When returned by students/overview API */
    firstLogin?: string | null;
    groupSettings?: GroupSettings;
  };
  progress: {
    level: number;
    userStreak: Streak[];
    unitsProgress: UnitProgress[];
    totalProgressInLevel?: number;
  };
  /** When returned by students/overview API (aligned with teacher ClassOverviewData) */
  userSessionInfo?: {
    firstLogin?: string;
    lastLogin?: string;
  };
}

export interface StudentReportContent {
  userId: string;
  organizationId: string;
  groupId: string;
  userLevel: number;
  overallProgressInLevel: number;
  passRate: number;
  failureRate: number;
  hasSkippedActivities: boolean;
  engagement: {
    total: number;
    totalInPrev7Days: number;
    averagePerWeek: number;
  };
  progress: {
    totalActivitiesCompletedInLevel: number;
    totalActivitiesCompletedInTheLast7Days: number;
    averageActivitysPerWeek: number;
  };
  attemptsAndHints: {
    totalHintsUsed: number;
    averageHintsPerActivity: number;
    averageAttemptsPerActivity: number;
  };
  streakLength: {
    longestStreak: number;
    strakLast7Days: number;
    averageStreakPerWeek: number;
  };
  avgSkillsScore: {
    skillId: number;
    skillScore: number;
  }[];
}

export type StudentProgress = {
  userId: string;
  userLevel: number;
  coins: number;
  completedUnits: number;
  completedTopics: number;
  completedActivities: number;
  achievedStars: number;
  unit: {
    topicsOverviewPerUnit: {
      [unitId: string]: {
        [topicId: string]: {
          topicId: number;
          achievedStars: number;
          completedActivitiesCount: number;
          isAllActivitiesCompleted?: boolean;
          canDo?: {
            userAnswered: number;
          };
        };
      };
    };
  };
  topic: {
    activitiesOverviewPerTopic: {
      [topicId: string]: {
        [activityId: string]: {
          activityId: number;
          lastTimePlayed: string;
          cumulativeCorrectAnswers: number;
          cumulativeAchievedStars: number;
          cumulativeScore: number;
          attempts: number;
          isCompleted: boolean;
          isSkipped: boolean;
          hasScore: boolean;
        };
      };
    };
  };
};
